import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import axiosInstance from "../../../services/api";
import { toast } from "react-toastify";
import Modal from "../../../components/generic/modal";
import statsService from "../../../services/statsService";
import { formatShortDate } from "../../../helpers/functions";
import { listEventPhotoKeys, removePhotoKeys } from "../../../helpers/myPhotos";
import { notifyPhotosChanged } from "../../../helpers/photosChanged";
import { confirmPublished, keysWithStatus, RETRY_LATER, withoutPublicPrefix } from "../../../helpers/publicPhotoChanges";
import EventsService from "../../../services/eventsService";
import { cx, RevButton, RevCard, RevChip, RevDanger, RevEmpty, RevFigure, RevKvRow, RevModal, RevProgress, RevSegmented, RevSkeleton } from "../../../components/ds";

const SORT_OPTIONS = [
    { value: "photoDate", labelKey: "eventGallery.filters.sortPhotoDate" },
    { value: "photographer", labelKey: "eventGallery.filters.sortPhotographer" },
    { value: "filename", labelKey: "eventGallery.filters.sortFilename" },
];

const DIR_OPTIONS = [
    { value: "desc", labelKey: "eventGallery.filters.dirDesc" },
    { value: "asc", labelKey: "eventGallery.filters.dirAsc" },
];

const VISIBILITY_OPTIONS = [
    { value: "", labelKey: "eventGallery.filters.allVisibility" },
    { value: "public", labelKey: "eventGallery.filters.public" },
    { value: "private", labelKey: "eventGallery.filters.private" },
];

const truncateFileName = (name) => {
    if (!name) return "";
    return (name.length > 20 ? "..." : "") + name.slice(-20);
};

const MiniaturaGaleria = ({ src, alt, seleccionada }) => {
    const ref = useRef(null);
    const [cargada, setCargada] = useState(false);

    useLayoutEffect(() => {
        const img = ref.current;
        setCargada(!!(img && img.complete && img.naturalWidth > 0));
    }, [src]);

    return (
        <div className={cx('rev-tile', seleccionada && 'rev-tile--selected', cargada ? 'is-cargada' : 'rev-skel rev-skel--dark')}>
            <img ref={ref} className="rev-tile__img" src={src} alt={alt} loading="lazy" onLoad={() => setCargada(true)} onError={() => setCargada(true)} />
        </div>
    );
};

// Visor a pantalla de la foto seleccionada: solo la foto y las flechas. Muestra la miniatura
// con marca de agua (960 px de lado largo, sin estirarla). ← → recorren la página actual;
// se cierra con Escape o con un clic fuera de la foto.
const VisorFoto = ({ photo, index, total, onPrev, onNext, onClose, keysBlocked }) => {
    const { t } = useTranslation();
    const latest = useRef(null);
    latest.current = { onPrev, onNext, keysBlocked };

    useEffect(() => {
        const handleKeyDown = (e) => {
            const { onPrev, onNext, keysBlocked } = latest.current;
            if (keysBlocked) return;
            if (e.key === 'ArrowLeft' && onPrev) {
                e.preventDefault();
                onPrev();
            } else if (e.key === 'ArrowRight' && onNext) {
                e.preventDefault();
                onNext();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const closeOnBackdrop = (e) => {
        if (e.target === e.currentTarget) onClose(e);
    };

    return (
        <RevModal className="rev-viewer" data-register="dark" onClose={onClose} closeLabel={t('eventGallery.viewer.close')}>
            <div className="rev-viewer__head">
                <span className="rev-mono rev-viewer__name">{photo.originalFileName || photo.id}</span>
                <span className="rev-mono rev-num">{t('eventGallery.viewer.position', { index: index + 1, total })}</span>
            </div>
            <div className="rev-viewer__stage" onClick={closeOnBackdrop}>
                <img key={photo.id} className="rev-viewer__img" src={photo.url} alt={photo.originalFileName} />
                <RevButton variant="secondary" icon className="rev-viewer__nav rev-viewer__nav--prev" onClick={onPrev} disabled={!onPrev}
                    aria-label={t('eventGallery.viewer.prev')}>
                    <span className="material-icons">chevron_left</span>
                </RevButton>
                <RevButton variant="secondary" icon className="rev-viewer__nav rev-viewer__nav--next" onClick={onNext} disabled={!onNext}
                    aria-label={t('eventGallery.viewer.next')}>
                    <span className="material-icons">chevron_right</span>
                </RevButton>
            </div>
            <span className="rev-viewer__meta">
                {photo.photographerEmail}{photo.photoDate || photo.uploadDate ? ` · ${photo.photoDate || photo.uploadDate}` : ''}
            </span>
        </RevModal>
    );
};

const PhotoGallery = ({ user, selectedEvent, guest, sharedEvent }) => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [rotatingPhoto, setRotatingPhoto] = useState(false);
    const [modalDeletePhoto, setModalDeletePhoto] = useState(false);
    const [onlyOnePhotographer, setOnlyOnePhotographer] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [photographers, setPhotographers] = useState([]);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [totalAmountOrders, setTotalAmountOrders] = useState(null);
    const [myPhotoKeys, setMyPhotoKeys] = useState(null);
    const [loadingMyPhotos, setLoadingMyPhotos] = useState(true);
    const myPhotosRequest = useRef(0);
    const [deleteSelection, setDeleteSelection] = useState(null);
    const [preparingDeletion, setPreparingDeletion] = useState(false);
    const [deletingPhotos, setDeletingPhotos] = useState(false);
    const [deleteCounter, setDeleteCounter] = useState(0);
    const [deleteTotal, setDeleteTotal] = useState(0);
    // Fotos visibles en la galería pública (solo el dueño las gestiona); null = sin consultar.
    const [publicKeys, setPublicKeys] = useState(null);
    const [publicBusy, setPublicBusy] = useState(false);
    // Claves a despublicar pendientes de confirmar (una foto o la selección).
    const [unpublishRequest, setUnpublishRequest] = useState(null);
    // Publicaciones aceptadas que la galería aún no muestra.
    const [publishingKeys, setPublishingKeys] = useState(() => new Set());
    // Selección múltiple para publicar/despublicar; se conserva al cambiar de página o de filtro.
    const [checkedKeys, setCheckedKeys] = useState(() => new Set());
    const bulkBusy = useRef(false);
    const photoRequest = useRef(0);
    const deletedKeys = useRef(new Set());
    const activeEventId = useRef(id);
    activeEventId.current = id;

    const [filters, setFilters] = useState({
        dateFrom: undefined,
        dateTo: undefined,
        photographer: "",
        visibility: "",
        sortBy: "photoDate",
        sortDirection: "desc",
        pageSize: 12,
        page: 1
    });

    // La tarjeta de precios e ingresos es del dueño del evento; el invitado no la ve.
    const showPricingCard = !guest;
    // El invitado solo edita (rota, borra) las fotos que subió él: `UserId` de la foto = segmento
    // FtpUsers/<userId> de su clave S3, el mismo id con el que Subir fotos escribe la clave.
    const isMine = (photo) => !!photo && photo.photographerId === user?.id;
    const canEditSelected = !guest || isMine(selectedPhoto);
    const eventInfo = guest ? sharedEvent : selectedEvent;
    const canDeletePhotos = !!user?.id && !!id && eventInfo?.id === id;
    const bulkLocked = preparingDeletion || deletingPhotos;
    const locale = i18n.language;
    const currencyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency: user?.currency || "EUR" });
    const eventDateFormatted = formatShortDate(eventInfo?.dateFrom, locale);
    const photoKey = (photo) => `${id}/FtpUsers/${photo?.photographerId}/${photo?.id}`;
    // La galería pública cambia siempre, también con los selfies activos.
    const canChangePublic = !guest && !!publicKeys;
    const isPublic = (photo) => !!publicKeys?.has(photoKey(photo));

    const refreshPublicKeys = useCallback(async () => {
        const { data } = await EventsService.publicPhotos(id);
        const listed = new Set((data?.photos || []).map(p => withoutPublicPrefix(p.photoKey)));
        if (activeEventId.current === id) setPublicKeys(listed);
        return listed;
    }, [id]);

    useEffect(() => {
        setPublicKeys(null);
        if (!guest && id) refreshPublicKeys().catch(error => console.error('Error loading public photos:', error));
    }, [guest, id, refreshPublicKeys]);

    // Si la foto del visor desaparece (borrada, rotada, otra página), el visor no se reabre solo.
    useEffect(() => {
        if (viewerOpen && !photos.some(photo => photo.id === selectedPhoto?.id)) setViewerOpen(false);
    }, [viewerOpen, photos, selectedPhoto]);

    const onPublicError = (error) => {
        console.error('[PUBLIC]', error);
        toast.error(t('publicGallery.changes.error'));
    };

    const withKeys = (keys, change) => current => { const next = new Set(current); keys.forEach(key => change(next, key)); return next; };
    const uncheck = keys => setCheckedKeys(withKeys(keys, (set, key) => set.delete(key)));

    // Publica y despublica una foto o la selección: mismo endpoint, mismo cupo previo y mismos
    // resúmenes que Subir fotos. Lo que falla u otra operación retenía queda seleccionado para reintentar.
    const publishKeys = async (keys) => {
        const eventId = id;
        const pending = keys.filter(key => !publishingKeys.has(key) && !publicKeys?.has(key));
        if (publicBusy || pending.length === 0) return;
        setPublicBusy(true);
        try {
            const { data: capacity } = await EventsService.publicUploads(eventId);
            if (pending.length > capacity.available) {
                toast.error(t('publicGallery.batchFull', { available: capacity.available, selected: pending.length }));
                return;
            }
            const { results } = await EventsService.publishPublic(eventId, pending);
            if (activeEventId.current !== eventId) return;
            const accepted = keysWithStatus(results, 'accepted');
            const already = keysWithStatus(results, 'alreadyPublic');
            const support = keysWithStatus(results, 'needsSupport').length;
            const busy = keysWithStatus(results, ...RETRY_LATER).length;
            const failed = keysWithStatus(results, 'failed', 'notFound').length;
            uncheck([...accepted, ...already]);
            if (already.length) setPublicKeys(withKeys(already, (set, key) => set.add(key)));
            if (support) toast.error(t('publicGallery.changes.needsSupport', { count: support }));
            if (busy) toast.info(t('publicGallery.changes.busy', { count: busy }));
            if (failed) toast.error(t('publicGallery.publish.failed', { count: failed }));
            if (accepted.length === 0) return;
            toast.info(t('publicGallery.publish.inProgress', { count: accepted.length }));
            setPublishingKeys(withKeys(accepted, (set, key) => set.add(key)));
            confirmPublished(() => EventsService.publicPhotos(eventId), accepted, { isCancelled: () => activeEventId.current !== eventId })
                .then(result => {
                    if (activeEventId.current !== eventId) return;
                    setPublishingKeys(withKeys(accepted, (set, key) => set.delete(key)));
                    setPublicKeys(result.listed);
                    if (result.added) toast.success(t('publicGallery.publish.done', { count: result.added }));
                    if (result.pending) toast.info(t(result.full ? 'publicGallery.publish.pendingFull' : 'publicGallery.publish.pending', { count: result.pending }));
                });
        } catch (error) {
            onPublicError(error);
        } finally {
            setPublicBusy(false);
        }
    };

    const onUnpublish = async () => {
        const eventId = id;
        const keys = unpublishRequest || [];
        setPublicBusy(true);
        try {
            const { results } = await EventsService.unpublishPublic(eventId, keys);
            if (activeEventId.current !== eventId) return;
            const gone = keysWithStatus(results, 'unpublished', 'notPublic');
            const removed = keysWithStatus(results, 'unpublished').length;
            const support = keysWithStatus(results, 'needsSupport').length;
            const busy = keysWithStatus(results, ...RETRY_LATER).length;
            const failed = keysWithStatus(results, 'failed', 'notFound').length;
            setPublicKeys(current => current && withKeys(gone, (set, key) => set.delete(key))(current));
            uncheck(gone);
            if (removed) toast.success(t('publicGallery.unpublish.done', { count: removed }));
            if (support) toast.error(t('publicGallery.changes.needsSupport', { count: support }));
            if (busy) toast.info(t('publicGallery.changes.busy', { count: busy }));
            if (failed) toast.error(t('publicGallery.unpublish.failed', { count: failed }));
        } catch (error) {
            onPublicError(error);
        } finally {
            setUnpublishRequest(null);
            setPublicBusy(false);
        }
    };

    const loadPhotoKeys = useCallback(async (eventId, userId) => {
        const request = ++myPhotosRequest.current;
        try {
            const result = await listEventPhotoKeys(eventId, userId);
            if (request === myPhotosRequest.current && activeEventId.current === eventId) setMyPhotoKeys(result.mine);
            return result;
        } catch (error) {
            if (request === myPhotosRequest.current && activeEventId.current === eventId) setMyPhotoKeys(null);
            throw error;
        } finally {
            if (request === myPhotosRequest.current && activeEventId.current === eventId) setLoadingMyPhotos(false);
        }
    }, []);

    useEffect(() => {
        setMyPhotoKeys(null);
        setLoadingMyPhotos(!!(id && user?.id));
        if (id && user?.id) {
            loadPhotoKeys(id, user.id).catch(error => console.error('Error counting my photos:', error));
        }
        return () => { myPhotosRequest.current += 1; };
    }, [id, user?.id, loadPhotoKeys]);

    useEffect(() => {
        let alive = true;
        if (showPricingCard && id) {
            statsService.getTotalAmountOrders(id).then((response) => {
                if (alive) setTotalAmountOrders(response?.data?.Data ?? null);
            }).catch((error) => {
                console.error("Error fetching total amount orders:", error);
            });
        }
        return () => {
            alive = false;
        };
        // eslint-disable-next-line
    }, [id, showPricingCard]);

    useEffect(() => {
        activeEventId.current = id;
        deletedKeys.current = new Set();
        setCheckedKeys(new Set());
        setPublishingKeys(new Set());
        setUnpublishRequest(null);
        setDeleteSelection(null);
        setSelectedPhoto(null);
        setPhotos([]);
        return () => {
            photoRequest.current += 1;
            activeEventId.current = null;
        };
    }, [id]);

    useEffect(() => {
        if (id && user && !bulkLocked) {
            // Debounce para evitar múltiples llamadas al cambiar filtros
            const debounceTimeout = setTimeout(() => {
                fetchPhotos();
            }, 300);

            return () => clearTimeout(debounceTimeout);
        }
        // eslint-disable-next-line
    }, [id, user, filters, bulkLocked, filters.visibility ? publicKeys : null]);

    const fetchPhotos = async () => {
        const request = ++photoRequest.current;
        try {
            setLoading(true);

            // Preparar los filtros para el backend

            const bothDatesOk = filters.dateFrom > 1 && filters.dateTo > 1 && filters.dateFrom < filters.dateTo;

            const requestFilters = {
                ...filters,
                // Solo convertir las fechas si tienen valor
                dateFrom: bothDatesOk ? filters.dateFrom : undefined,
                dateTo: bothDatesOk ? filters.dateTo : undefined,
                // Añadir explícitamente la paginación
                page: filters.page,
                pageSize: filters.pageSize
            };

            const response = await axiosInstance.post(
                `/photos-in-event-for-gallery/${id}`,
                requestFilters
            );

            const { photos: photosData, photographers: photographersData } = response.data;

            // Crear mapa de fotógrafos
            const photographerMap = Object.fromEntries(
                photographersData?.map(p => [p.id, p.email]) || []
            );

            // Mapear las fotos con la información del fotógrafo
            const mappedPhotos = photosData.map(photo => ({
                id: photo.Id,
                url: photo.S3SignedUrl,
                photographerId: photo.UserId,
                photographerEmail: photographerMap[photo.UserId] || 'Unknown',
                filename: photo.Filename,
                originalFileName: photo.OriginalFileName,
                size: photo.Size,
                bucket: photo.Bucket,
                uploadDate: photo.UploadDate ? new Date(photo.UploadDate).toLocaleString(i18n.language) : null,
                photoDate: photo.PhotoDate ? new Date(parseInt(photo.PhotoDate)).toLocaleString(i18n.language) : null
            }));

            if (request !== photoRequest.current) return;
            // Una clave hash puede volver a subirse. Consultar S3 solo si la página trae
            // claves borradas: DynamoDB conserva UploadDate incluso al reponer la foto.
            if (mappedPhotos.some(photo => deletedKeys.current.has(`${id}/FtpUsers/${photo.photographerId}/${photo.id}`))) {
                try {
                    const { all } = await loadPhotoKeys(id, user.id);
                    if (request !== photoRequest.current) return;
                    all.forEach(key => deletedKeys.current.delete(key.replace(/^public\//, '')));
                } catch (error) {
                    console.error('Error checking reuploaded photos:', error);
                }
            }
            if (request !== photoRequest.current) return;
            // S3 borra antes de que DynamoDB actualice la galería: no resucitar fotos borradas.
            setPhotos(mappedPhotos.filter(photo => !deletedKeys.current.has(`${id}/FtpUsers/${photo.photographerId}/${photo.id}`)));

            // Actualizar lista de fotógrafos si no la teníamos
            if (!photographers.length && photographersData?.length) {
                setPhotographers(photographersData);
                setOnlyOnePhotographer(photographersData.length === 1);
            }

        } catch (error) {
            console.error("Error fetching photos:", error);
        } finally {
            if (request === photoRequest.current) setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            // Reset a la primera página cuando cambian los filtros
            page: key === 'page' ? value : 1
        }));
    };

    const handleRotatePhoto = async () => {
        setRotatingPhoto(!rotatingPhoto);
    };

    const handleDownloadPhoto = async () => {
        try {
            setLoadingDownload(true);
            const response = await axiosInstance.post('/download-one-photo-from-gallery', {
                path: "public/" + id + "/FtpUsers/" + selectedPhoto?.photographerId + "/" + selectedPhoto?.id,
                originalFileName: selectedPhoto?.originalFileName ?? selectedPhoto?.id
            });

            // Get presigned URL from response
            const { url } = response.data;

            // Create hidden anchor element for download
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = selectedPhoto?.originalFileName || 'photo';

            // Add to document, click and cleanup
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

        } catch (error) {
            console.error('Error downloading photo:', error);
            toast.error(t('eventGallery.selected.downloadError'));
        } finally {
            setLoadingDownload(false);
        }
    };

    const onDeletePhoto = async () => {
        try {
            setLoading(true);
            await axiosInstance.post('/delete-one-photo-from-gallery', {
                id: selectedPhoto?.id,
                eventId: id,
                path: "public/" + id + "/FtpUsers/" + selectedPhoto?.photographerId + "/" + selectedPhoto?.id
            });
            //filtrar la foto del estado
            setSelectedPhoto(null);
            setPhotos(photos.filter(photo => photo.id !== selectedPhoto.id));
            toast.success(t('eventGallery.selected.deleteSuccess'));
            notifyPhotosChanged(id);
            loadPhotoKeys(id, user.id).catch(error => console.error('Error updating my photo count:', error));
        } catch (error) {
            console.error('Error deleting photo:', error);
            toast.error(t('eventGallery.selected.deleteError'));
            // Mostrar error al usuario
        } finally {
            setModalDeletePhoto(false);
            setLoading(false);
        }
    }

    const handleDeletePhoto = async () => {
        setModalDeletePhoto(true);
    };

    const prepareDeletePhotos = async () => {
        if (!canDeletePhotos || bulkBusy.current) return;
        bulkBusy.current = true;
        setPreparingDeletion(true);
        try {
            const { mine, all } = await loadPhotoKeys(id, user.id);
            if (activeEventId.current !== id) return;
            const keys = guest ? mine : all;
            if (keys.length) setDeleteSelection({ eventId: id, userId: user.id, guest: !!guest, keys });
            else toast.info(t('eventGallery.danger.empty'));
        } catch (error) {
            console.error('Error listing photos to delete:', error);
            if (activeEventId.current === id) toast.error(t('eventGallery.danger.listError'));
        } finally {
            bulkBusy.current = false;
            if (activeEventId.current !== null) setPreparingDeletion(false);
        }
    };

    const onDeletePhotos = async () => {
        if (bulkBusy.current || !canDeletePhotos || !deleteSelection || deleteSelection.eventId !== id
            || deleteSelection.userId !== user.id || deleteSelection.guest !== !!guest) return;
        const { keys, eventId, userId } = deleteSelection;
        bulkBusy.current = true;
        photoRequest.current += 1;
        setLoading(false);
        setDeleteSelection(null);
        setSelectedPhoto(null);
        setDeletingPhotos(true);
        setDeleteCounter(0);
        setDeleteTotal(keys.length);

        const hideDeletedPhotos = removed => {
            if (activeEventId.current !== eventId) return;
            const removedKeys = new Set(removed.map(key => key.replace(/^public\//, '')));
            removedKeys.forEach(key => deletedKeys.current.add(key));
            setMyPhotoKeys(previous => previous?.filter(key => !removedKeys.has(key.replace(/^public\//, ''))) ?? null);
            setPhotos(previous => previous.filter(photo => !deletedKeys.current.has(`${eventId}/FtpUsers/${photo.photographerId}/${photo.id}`)));
        };

        try {
            await removePhotoKeys(keys, { onProgress: count => {
                if (activeEventId.current !== eventId) return;
                setDeleteCounter(count);
                hideDeletedPhotos(keys.slice(0, count));
            } });
            hideDeletedPhotos(keys);
            if (activeEventId.current === eventId) toast.success(t('eventGallery.danger.deleteSuccess'));
            notifyPhotosChanged(eventId);
        } catch (error) {
            console.error('Error deleting photos:', error);
            if (activeEventId.current === eventId) toast.error(t('eventGallery.danger.deleteError'));
        } finally {
            // Un fallo puede llegar tras varios lotes o tras parte del último: comprobar lo que queda.
            try {
                const { all } = await loadPhotoKeys(eventId, userId);
                const remaining = new Set(all.map(key => key.replace(/^public\//, '')));
                hideDeletedPhotos(keys.filter(key => !remaining.has(key.replace(/^public\//, ''))));
            } catch (error) {
                console.error('Error refreshing photos after deletion:', error);
            }
            bulkBusy.current = false;
            if (activeEventId.current !== null) {
                setDeletingPhotos(false);
                setDeleteCounter(0);
            }
            if (activeEventId.current === eventId) setFilters(previous => ({ ...previous, page: 1 }));
        }
    };

    const RotatingPhoto = () => {

        const [rotation, setRotation] = useState(0);
        const [loadingRotation, setLoadingRotation] = useState(false);
        const [modalRotatePhoto, setModalRotatePhoto] = useState(false);
        const { t } = useTranslation();

        const onApplyRotatePhoto = async (rotation) => {
            setLoadingRotation(true);
            const path = 'public/' + selectedPhoto.url.split('?')[0].split('/public/')[1];
            const photo = {
                id: selectedPhoto.id,
                rotation: rotation,
                path: path
            };

            try {
                await axiosInstance.post('/rotate-one-photo-from-key', photo);

                // add bolean to this photo in the list of photos with the key "editing"
                const currentId = selectedPhoto.id;
                setPhotos(photos.map(photo => ({
                    ...photo,
                    editing: photo.id === currentId
                })));
            } catch (error) {
                console.error('Error rotating photo:', error);
            } finally {
                setRotatingPhoto(false);
                setSelectedPhoto(null);
            }
        };

        const rotateLeft = () => {
            setRotation((prev) => (prev - 90) % 360);
        };

        const rotateRight = () => {
            setRotation((prev) => (prev + 90) % 360);
        };

        if (loadingRotation) {
            return <RevSkeleton.Cuerpo
                tipo="imagen"
                titulo={t('eventGallery.rotate.title')}
                ayuda={t('eventGallery.rotate.help')}
                controles={['rotate_left', 'rotate_right']}
                acciones={[t('generic.alertButton.cancel'), t('eventGallery.rotate.apply')]}
                clases={{
                    contenedor: 'rev-rotating',
                    cabecera: 'rev-rotating__head',
                    preview: 'rev-rotating__preview',
                    controles: 'rev-rotating__controls',
                    acciones: 'rev-rotating__actions',
                }}
                label={t('event.photos.loading')} />;
        }

        const normalizedRotation = ((rotation % 360) + 360) % 360;

        return (
            <div className="rev-rotating">
                {modalRotatePhoto &&
                    <Modal
                        title={t('eventGallery.rotate.modalTitle')}
                        message={t('eventGallery.rotate.modalMessage')}
                        showCancelButton={true}
                        showConfirmButton={true}
                        confirmButtonText={t('eventGallery.rotate.confirmYes')}
                        cancelButtonText={t('eventGallery.rotate.confirmNo')}
                        onCancel={() => setModalRotatePhoto(false)}
                        onConfirm={() => onApplyRotatePhoto(rotation)}
                    />
                }
                <div className="rev-rotating__head">
                    <h2 className="rev-page-title">{t('eventGallery.rotate.title')}</h2>
                    <span className="rev-help">{t('eventGallery.rotate.help')}</span>
                </div>

                <div className="rev-rotating__preview">
                    <img
                        src={selectedPhoto.url}
                        alt={t('eventGallery.rotate.previewAlt')}
                        className={`rev-rotating__img rev-rotating__img--r${normalizedRotation}`}
                    />
                </div>

                <div className="rev-rotating__controls">
                    <RevButton variant="secondary" icon onClick={rotateLeft} aria-label={t('eventGallery.rotate.left')}>
                        <span className="material-icons rev-icon-sm">rotate_left</span>
                    </RevButton>
                    <RevButton variant="secondary" icon onClick={rotateRight} aria-label={t('eventGallery.rotate.right')}>
                        <span className="material-icons rev-icon-sm">rotate_right</span>
                    </RevButton>
                </div>

                <div className="rev-rotating__actions">
                    <RevButton variant="secondary" onClick={() => setRotatingPhoto(false)}>
                        {t('generic.alertButton.cancel')}
                    </RevButton>
                    <RevButton variant="primary" disabled={rotation === 0} onClick={() => setModalRotatePhoto(true)}>
                        {t('eventGallery.rotate.apply')}
                    </RevButton>
                </div>
            </div>
        );
    };

    if (rotatingPhoto) {
        return <RotatingPhoto />;
    }

    const isFiltered = !!filters.photographer || !!filters.visibility;
    const pageCount = photos.length;
    const checked = canChangePublic ? [...checkedKeys] : [];
    const checkedToPublish = checked.filter(key => !publicKeys.has(key) && !publishingKeys.has(key));
    const checkedToUnpublish = checked.filter(key => publicKeys.has(key));
    const pageKeys = photos.map(photoKey);
    const pageAllChecked = pageKeys.length > 0 && pageKeys.every(key => checkedKeys.has(key));
    const toggleChecked = key => setCheckedKeys(current => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });
    const togglePage = () => setCheckedKeys(withKeys(pageKeys, (set, key) => (pageAllChecked ? set.delete(key) : set.add(key))));
    const selectedIndex = selectedPhoto ? photos.findIndex(photo => photo.id === selectedPhoto.id) : -1;
    const openViewer = (photo) => {
        setSelectedPhoto(photo);
        setViewerOpen(true);
    };
    // Publicar/despublicar la foto seleccionada: la misma acción en la card y en el visor.
    const publicAction = (photo) => {
        if (!canChangePublic || !photo) return null;
        if (isPublic(photo)) {
            return { icon: 'public_off', label: t('eventGallery.selected.unpublish'), disabled: bulkLocked || publicBusy,
                onClick: () => setUnpublishRequest([photoKey(photo)]) };
        }
        const publishing = publishingKeys.has(photoKey(photo));
        return { icon: 'public', label: t(publishing ? 'eventGallery.selected.publishing' : 'eventGallery.selected.publish'),
            disabled: bulkLocked || publicBusy || publishing, onClick: () => publishKeys([photoKey(photo)]) };
    };
    const selectedPublicAction = publicAction(selectedPhoto);

    return (
        <div className="rev-gallery">
            <div className="rev-cols rev-cols--flush rev-gallery-header">
                <div className="rev-cols__main">

                    <RevCard className="rev-gallery-filters">
                        <div className="rev-gallery-filters__group">
                            <span className="rev-label">{t('eventGallery.filters.sortBy')}</span>
                            <div className="rev-gallery-filters__row">
                                <RevSegmented
                                    disabled={bulkLocked}
                                    items={SORT_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
                                    value={filters.sortBy}
                                    onChange={(value) => handleFilterChange('sortBy', value)}
                                />
                                <RevSegmented
                                    disabled={bulkLocked}
                                    items={DIR_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
                                    value={filters.sortDirection}
                                    onChange={(value) => handleFilterChange('sortDirection', value)}
                                />
                            </div>
                        </div>
                        <div className="rev-gallery-filters__line rev-gallery-filters__group--divider">
                            <div className="rev-gallery-filters__group">
                                <span className="rev-label">{t('eventGallery.filters.photographer')}</span>
                                <div className="rev-gallery-filters__chips">
                                    <RevChip
                                        as="button"
                                        type="button"
                                        disabled={onlyOnePhotographer || bulkLocked}
                                        tone={!filters.photographer ? 'active' : undefined}
                                        onClick={() => handleFilterChange('photographer', '')}
                                    >
                                        {t('eventGallery.filters.allPhotographers')}
                                    </RevChip>
                                    {photographers.map((photographer) => (
                                        <RevChip
                                            key={photographer.id}
                                            as="button"
                                            type="button"
                                            disabled={onlyOnePhotographer || bulkLocked}
                                            tone={filters.photographer === photographer.id ? 'active' : undefined}
                                            title={photographer.email}
                                            onClick={() => handleFilterChange('photographer', photographer.id)}
                                        >
                                            {photographer.email}
                                        </RevChip>
                                    ))}
                                </div>
                            </div>
                            {!guest && (
                                <div className="rev-gallery-filters__group">
                                    <span className="rev-label">{t('eventGallery.filters.visibility')}</span>
                                    <div className="rev-gallery-filters__chips">
                                        {VISIBILITY_OPTIONS.map((opt) => (
                                            <RevChip
                                                key={opt.value || 'all'}
                                                as="button"
                                                type="button"
                                                disabled={bulkLocked}
                                                tone={filters.visibility === opt.value ? 'active' : undefined}
                                                onClick={() => handleFilterChange('visibility', opt.value)}
                                            >
                                                {t(opt.labelKey)}
                                            </RevChip>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </RevCard>
                </div>
                <div className="rev-cols__aside">
                    <RevCard
                        title={t('eventPhotos.myPhotos.title')}
                        className="rev-gallery-summary"
                        role="region"
                        aria-label={t('eventPhotos.myPhotos.title')}
                        aria-busy={loadingMyPhotos}
                    >
                        <div className="rev-gallery-summary__figure">
                            <RevFigure size="xl" muted={!loadingMyPhotos && myPhotoKeys === null}>
                                {loadingMyPhotos ? <RevSkeleton kind="cifra" /> : myPhotoKeys?.length ?? '—'}
                            </RevFigure>
                            <span className="rev-help">{t('eventPhotos.myPhotos.pie', { count: myPhotoKeys?.length ?? 0 })}</span>
                        </div>
                    </RevCard>
                </div>
            </div>

            <div className="rev-cols rev-cols--flush">
                <div className="rev-cols__main">

                    {deletingPhotos && (
                        <RevCard>
                            <div className="rev-gallery-deleting">
                                <RevProgress value={deleteCounter} max={deleteTotal} />
                                <span className="rev-help" role="status">
                                    <span className="rev-num">{deleteCounter}</span>
                                    {" / "}
                                    <span className="rev-num">{deleteTotal}</span>
                                    {" "}
                                    {t('eventPhotos.myPhotos.deletingSuffix', { count: deleteCounter })}
                                </span>
                            </div>
                        </RevCard>
                    )}

                    {loading && !deletingPhotos && <RevSkeleton.Cuerpo
                        tipo="canvas"
                        miniaturas={filters.pageSize}
                        label={t('event.photos.loading')} />}

                    {!loading && !deletingPhotos && photos?.length === 0 && (
                        <RevEmpty
                            icon={isFiltered ? 'search_off' : 'photo_library'}
                            title={t(isFiltered ? 'eventGallery.empty.filteredTitle' : 'eventGallery.empty.title')}
                            body={t(isFiltered ? 'eventGallery.empty.filteredBody' : 'eventGallery.empty.body')}
                        >
                            {!isFiltered && (
                                <RevButton variant="primary" onClick={() => history.push(`/events/${id}/photos`)}>
                                    {t('eventGallery.empty.cta')}
                                </RevButton>
                            )}
                        </RevEmpty>
                    )}

                    {!loading && !deletingPhotos && photos?.length > 0 && (
                        <div className="rev-photocanvas" data-register="dark">
                            <div className="rev-photocanvas__head">
                                <span className="rev-mono">
                                    {eventInfo?.code}{eventDateFormatted ? ` · ${eventDateFormatted}` : ''}
                                </span>
                                {canChangePublic && (
                                    <div className="rev-gallery-bulk" role="group" aria-label={t('eventGallery.bulk.label')}>
                                        <RevButton size="sm" variant="secondary" onClick={togglePage} disabled={bulkLocked || publicBusy}>
                                            {t(pageAllChecked ? 'eventGallery.bulk.unselectPage' : 'eventGallery.bulk.selectPage')}
                                        </RevButton>
                                        {checked.length > 0 && <>
                                            <span className="rev-gallery-bulk__count" role="status">{t('eventGallery.bulk.selected', { count: checked.length })}</span>
                                            {checkedToPublish.length > 0 && (
                                                <RevButton size="sm" variant="primary" loading={publicBusy} disabled={bulkLocked || publicBusy}
                                                    onClick={() => publishKeys(checkedToPublish)}>
                                                    {t('eventGallery.bulk.publish', { count: checkedToPublish.length })}
                                                </RevButton>
                                            )}
                                            {checkedToUnpublish.length > 0 && (
                                                <RevButton size="sm" variant="secondary" disabled={bulkLocked || publicBusy}
                                                    onClick={() => setUnpublishRequest(checkedToUnpublish)}>
                                                    {t('eventGallery.bulk.unpublish', { count: checkedToUnpublish.length })}
                                                </RevButton>
                                            )}
                                            <RevButton size="sm" variant="secondary" disabled={publicBusy} onClick={() => setCheckedKeys(new Set())}>
                                                {t('eventGallery.bulk.clear')}
                                            </RevButton>
                                        </>}
                                    </div>
                                )}
                            </div>
                            <div className="rev-photocanvas__grid">
                                {photos.map((photo) => (
                                    <div key={photo.id} className="rev-photocanvas__cell" onClick={() => setSelectedPhoto(photo)} onDoubleClick={() => openViewer(photo)}>
                                        {canChangePublic && (
                                            <label className="rev-tile__check" onClick={event => event.stopPropagation()}>
                                                <input type="checkbox" checked={checkedKeys.has(photoKey(photo))} disabled={bulkLocked || publicBusy}
                                                    onChange={() => toggleChecked(photoKey(photo))}
                                                    aria-label={t('eventGallery.bulk.check', { name: photo.originalFileName || photo.id })} />
                                            </label>
                                        )}
                                        <MiniaturaGaleria src={photo.url} alt={photo.originalFileName} seleccionada={selectedPhoto?.id === photo.id} />
                                        <div className="rev-tile__meta">
                                            <span className="rev-tile__meta-name">{truncateFileName(photo.originalFileName)}</span>
                                            <span>{photo.photographerEmail} · {photo.photoDate || photo.uploadDate}</span>
                                            {photo.editing && <span className="rev-tile__meta-editing">{t('eventGallery.tile.editing')}</span>}
                                            {!guest && isPublic(photo) && <span className="rev-tile__meta-public">{t('eventGallery.tile.public')}</span>}
                                            {!guest && !isPublic(photo) && publishingKeys.has(photoKey(photo)) && <span className="rev-tile__meta-editing">{t('eventGallery.tile.publishing')}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="rev-photocanvas__foot">
                                <span className="rev-mono">{t('eventGallery.canvas.footer', { page: filters.page, count: pageCount })}</span>
                                <div className="rev-photocanvas__pagination">
                                    <RevButton size="sm" variant="secondary" onClick={() => handleFilterChange('page', 1)} disabled={filters.page <= 1 || loading || bulkLocked}>
                                        {t('eventGallery.pagination.first')}
                                    </RevButton>
                                    <RevButton
                                        size="sm" variant="secondary" icon
                                        onClick={() => handleFilterChange('page', filters.page - 1)}
                                        disabled={filters.page <= 1 || loading || bulkLocked}
                                        aria-label={t('eventGallery.pagination.prevAria')}
                                    >
                                        <span className="material-icons rev-icon-sm">chevron_left</span>
                                    </RevButton>
                                    <select
                                        className="rev-input rev-pagesize"
                                        disabled={bulkLocked}
                                        value={filters.pageSize}
                                        onChange={(e) => handleFilterChange('pageSize', Number(e.target.value))}
                                    >
                                        <option value={12}>12</option>
                                        <option value={24}>24</option>
                                    </select>
                                    <RevButton
                                        size="sm" variant="secondary" icon
                                        onClick={() => handleFilterChange('page', filters.page + 1)}
                                        disabled={photos?.length < filters.pageSize || loading || bulkLocked}
                                        aria-label={t('eventGallery.pagination.nextAria')}
                                    >
                                        <span className="material-icons rev-icon-sm">chevron_right</span>
                                    </RevButton>
                                    <RevButton
                                        size="sm" variant="secondary"
                                        onClick={() => handleFilterChange('page', filters.page + 1)}
                                        disabled={photos?.length < filters.pageSize || loading || bulkLocked}
                                    >
                                        {t('eventGallery.pagination.last')}
                                    </RevButton>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="rev-cols__aside">
                    {selectedPhoto && (
                        <RevCard title={t('eventGallery.selected.title')}>
                            <button type="button" className="rev-gallery-selected__zoom" onClick={() => openViewer(selectedPhoto)}
                                aria-label={t('eventGallery.selected.enlargeAria')}>
                                <img className="rev-gallery-selected__img" src={selectedPhoto.url} alt={selectedPhoto.originalFileName} />
                                <span className="material-icons rev-gallery-selected__zoom-icon" aria-hidden="true">open_in_full</span>
                            </button>
                            <span className="rev-mono rev-gallery-selected__name">{selectedPhoto.originalFileName}</span>
                            <RevKvRow k={t('eventGallery.selected.photographer')} v={selectedPhoto.photographerEmail || '—'} />
                            <RevKvRow k={t('eventGallery.selected.size')} v={selectedPhoto.size > 0 ? (selectedPhoto.size / 1024 / 1024).toFixed(2) + ' MB' : '—'} num />
                            <RevKvRow k={t('eventGallery.selected.photoDate')} v={selectedPhoto.photoDate || '—'} num />
                            <RevKvRow k={t('eventGallery.selected.uploadDate')} v={selectedPhoto.uploadDate || '—'} num />
                            <div className="rev-gallery-selected__actions">
                                <button type="button" className="rev-action-row" onClick={handleDownloadPhoto} disabled={loadingDownload}>
                                    <span className="material-icons rev-icon-sm">file_download</span>
                                    {loadingDownload ? t('eventGallery.selected.downloading') : t('eventGallery.selected.download')}
                                </button>
                                {canEditSelected && (
                                    <button type="button" className="rev-action-row" onClick={handleRotatePhoto} disabled={bulkLocked}>
                                        <span className="material-icons rev-icon-sm">rotate_left</span>
                                        {t('eventGallery.selected.rotate')}
                                    </button>
                                )}
                                {selectedPublicAction && (
                                    <button type="button" className="rev-action-row" onClick={selectedPublicAction.onClick} disabled={selectedPublicAction.disabled}>
                                        <span className="material-icons rev-icon-sm">{selectedPublicAction.icon}</span>
                                        {selectedPublicAction.label}
                                    </button>
                                )}
                                {canEditSelected && (
                                    <button type="button" className="rev-action-row rev-action-row--danger" onClick={handleDeletePhoto} disabled={bulkLocked}>
                                        <span className="material-icons rev-icon-sm">delete</span>
                                        {t('eventGallery.selected.delete')}
                                    </button>
                                )}
                                {guest && !canEditSelected && (
                                    <span className="rev-help">{t('eventGallery.selected.notMine')}</span>
                                )}
                            </div>
                        </RevCard>
                    )}

                    {showPricingCard && (
                        <RevCard title={t('eventGallery.pricing.title')}>
                            <RevKvRow k={t('eventGallery.pricing.unit')} num
                                v={selectedEvent?.pricingData?.unitPrice != null ? currencyFormatter.format(selectedEvent.pricingData.unitPrice) : '—'} />
                            <RevKvRow k={t('eventGallery.pricing.pack')} num
                                v={selectedEvent?.pricingData?.fullSelfiePrice > 0 ? currencyFormatter.format(selectedEvent.pricingData.fullSelfiePrice) : t('eventGallery.pricing.packOff')} />
                            <RevKvRow k={t('eventGallery.pricing.revenue')} num
                                v={totalAmountOrders != null ? currencyFormatter.format(totalAmountOrders) : '—'} />
                        </RevCard>
                    )}
                </div>
            </div>

            <RevDanger
                title={t('eventGallery.danger.title')}
                text={t(guest ? 'eventGallery.danger.descriptionMine' : 'eventGallery.danger.descriptionAll')}
                action={<RevButton
                    variant="danger"
                    loading={bulkLocked}
                    disabled={!canDeletePhotos || loading || loadingDownload || bulkLocked}
                    onClick={prepareDeletePhotos}
                >
                    {t(guest ? 'eventPhotos.myPhotos.deleteMine' : 'eventGallery.danger.deleteAll')}
                </RevButton>}
            />

            {deleteSelection && deleteSelection.eventId === id &&
                <Modal
                    title={t(deleteSelection.guest ? 'eventPhotos.myPhotos.deleteModal.title' : 'eventGallery.danger.deleteAll')}
                    message={t(deleteSelection.guest ? 'eventGallery.danger.confirmMine' : 'eventGallery.danger.confirmAll', { count: deleteSelection.keys.length })}
                    showCancelButton={true}
                    showConfirmButton={true}
                    onCancel={() => setDeleteSelection(null)}
                    onConfirm={onDeletePhotos}
                    isDanger={true}
                    confirmButtonText={t('generic.alertButton.delete')}
                    cancelButtonText={t('generic.alertButton.cancel')}
                />}

            {viewerOpen && selectedIndex >= 0 &&
                <VisorFoto
                    photo={selectedPhoto}
                    index={selectedIndex}
                    total={photos.length}
                    onPrev={selectedIndex > 0 ? () => setSelectedPhoto(photos[selectedIndex - 1]) : null}
                    onNext={selectedIndex < photos.length - 1 ? () => setSelectedPhoto(photos[selectedIndex + 1]) : null}
                    onClose={() => setViewerOpen(false)}
                    keysBlocked={!!unpublishRequest}
                />}

            {modalDeletePhoto &&
                <Modal
                    title={t('eventGallery.deleteModal.title')}
                    message={t('eventGallery.deleteModal.message')}
                    showCancelButton={true}
                    showConfirmButton={true}
                    onCancel={() => setModalDeletePhoto(false)}
                    onConfirm={onDeletePhoto}
                    loading={loading}
                    isDanger={true}
                    confirmButtonText={t('generic.alertButton.delete')}
                    cancelButtonText={t('generic.alertButton.cancel')}
                />}
            {unpublishRequest &&
                <Modal
                    title={t('publicGallery.unpublish.confirmTitle', { count: unpublishRequest.length })}
                    message={t(unpublishRequest.length > 1 ? 'eventGallery.bulk.unpublishBody' : 'eventGallery.selected.unpublishBody')}
                    showCancelButton={true}
                    showConfirmButton={true}
                    onCancel={() => setUnpublishRequest(null)}
                    onConfirm={onUnpublish}
                    loading={publicBusy}
                    isDanger={true}
                    confirmButtonText={t('eventGallery.selected.unpublishConfirm')}
                    cancelButtonText={t('generic.alertButton.cancel')}
                />}
        </div>
    );
};

const mapStateToProps = state => ({
    user: state.usersReducer.user,
    selectedEvent: state.eventsReducer.selectedEvent
});

export default connect(mapStateToProps)(PhotoGallery);
